/**
 * React Query hooks for leave management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  leaveAPI,
  CreateLeaveRequestPayload,
  LeaveStatus,
} from '../api/leave.api';

export const leaveKeys = {
  all: ['leave'] as const,
  balance: (year?: number) => [...leaveKeys.all, 'balance', year ?? 'current'] as const,
  myRequests: (year?: number) => [...leaveKeys.all, 'requests', 'me', year ?? 'all'] as const,
  allRequests: (filters?: object) => [...leaveKeys.all, 'requests', 'all', filters] as const,
  holidays: (range?: object) => [...leaveKeys.all, 'holidays', range] as const,
  myConfig: (year?: number) => [...leaveKeys.all, 'config', 'me', year ?? 'current'] as const,
  configs: (year?: number) => [...leaveKeys.all, 'configs', year ?? 'current'] as const,
  approvedDays: (range?: object) => [...leaveKeys.all, 'approved-days', range] as const,
};

// ---------- Balance ----------

export const useMyLeaveBalance = (year?: number) =>
  useQuery({
    queryKey: leaveKeys.balance(year),
    queryFn: () => leaveAPI.myBalance(year),
    staleTime: 60_000,
  });

// ---------- Requests ----------

export const useMyLeaveRequests = (year?: number) =>
  useQuery({
    queryKey: leaveKeys.myRequests(year),
    queryFn: () => leaveAPI.listMine(year),
    staleTime: 30_000,
  });

export const useAllLeaveRequests = (filters: { status?: LeaveStatus; userId?: string; year?: number } = {}) =>
  useQuery({
    queryKey: leaveKeys.allRequests(filters),
    queryFn: () => leaveAPI.listAll(filters),
    staleTime: 30_000,
  });

export const useCreateLeaveRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateLeaveRequestPayload) => leaveAPI.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
};

export const useCancelLeaveRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => leaveAPI.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
};

export const useDecideLeaveRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: 'approved' | 'denied'; reason?: string }) =>
      leaveAPI.decide(id, { status, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
};

// ---------- Holidays ----------

export const useHolidays = (range: { from?: string; to?: string } = {}) =>
  useQuery({
    queryKey: leaveKeys.holidays(range),
    queryFn: () => leaveAPI.listHolidays(range),
    staleTime: 24 * 60 * 60_000,  // legal holidays don't move, 1-day cache
  });

// ---------- Configs ----------

export const useMyLeaveConfig = (year?: number) =>
  useQuery({
    queryKey: leaveKeys.myConfig(year),
    queryFn: () => leaveAPI.getMyConfig(year),
    staleTime: 5 * 60_000,
  });

export const useAllLeaveConfigs = (year?: number) =>
  useQuery({
    queryKey: leaveKeys.configs(year),
    queryFn: () => leaveAPI.listConfigs(year),
    staleTime: 60_000,
  });

export const useUpsertLeaveConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...payload }: {
      userId: string;
      year?: number;
      legalLeaveDays?: number;
      baseHoursPerWeek?: number;
      recupTrackingStart?: string;
      employmentType?: 'employee' | 'independent';
      workingDays?: string;
    }) => leaveAPI.upsertConfig(userId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
};

// ---------- Approved days (for calendar grey-out) ----------

export const useMyApprovedLeaveDays = (range: { from?: string; to?: string } = {}) =>
  useQuery({
    queryKey: leaveKeys.approvedDays(range),
    queryFn: () => leaveAPI.myApprovedDays(range),
    staleTime: 60_000,
  });

// ---------- Weekly validations ----------

export const useMyUnvalidatedWeeks = (year?: number) =>
  useQuery({
    queryKey: [...leaveKeys.all, 'weeks-unvalidated', year ?? 'current'],
    queryFn: () => leaveAPI.myUnvalidatedWeeks(year),
    staleTime: 60_000,
  });

export const useMyValidatedWeeks = (year?: number) =>
  useQuery({
    queryKey: [...leaveKeys.all, 'weeks-validated', year ?? 'current'],
    queryFn: () => leaveAPI.myValidatedWeeks(year),
    staleTime: 60_000,
  });

export const useValidateWeek = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weekStart: string) => leaveAPI.validateWeek(weekStart),
    onSuccess: () => qc.invalidateQueries({ queryKey: leaveKeys.all }),
  });
};

export const useUnvalidateWeek = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weekStart: string) => leaveAPI.unvalidateWeek(weekStart),
    onSuccess: () => qc.invalidateQueries({ queryKey: leaveKeys.all }),
  });
};
